/* ──────────────────────────────────────────────────────────────────────
   TwinMaqPage — cinematic sales-platform page for /twinmaq/
   ────────────────────────────────────────────────────────────────────── */

const { useState: useTmqState, useEffect: useTmqEffect, useRef: useTmqRef } = React;

/* ── Arabic localization helpers (path-based; EN branch byte-identical) ── */
function dzIsAr() { return typeof window !== 'undefined' && window.location.pathname.startsWith('/ar/'); }
function dzT(en, ar) { return dzIsAr() ? ar : en; }

function useTmqReveal(threshold) {
  const ref = useTmqRef(null);
  useTmqEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          el.classList.add('is-in-view');
          io.disconnect();
        }
      });
    }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return ref;
}

/* ── Inline SVG glyphs for the feature tiles ──────────────────────────── */
function TmqGlyph({ name }) {
  const common = {
    width:'40', height:'40', viewBox:'0 0 64 64',
    fill:'none', stroke:'currentColor', strokeWidth:'1.75',
    strokeLinecap:'round', strokeLinejoin:'round'
  };
  switch (name) {
    case 'flythrough': return (
      <svg {...common}>
        <path d="M6 44 L20 36 L32 40 L46 28 L58 22" />
        <circle cx="32" cy="40" r="3" fill="currentColor" stroke="none">
          <animateMotion dur="3.6s" repeatCount="indefinite">
            <mpath href="#tmq-fp" />
          </animateMotion>
        </circle>
        <path id="tmq-fp" d="M6 44 L20 36 L32 40 L46 28 L58 22" style={{ display:'none' }} />
        <path d="M50 22 L58 22 L58 30" />
      </svg>
    );
    case 'city': return (
      <svg {...common}>
        {/* Skyline of buildings with pulsing aerial pin above */}
        <rect x="6"  y="36" width="10" height="22" />
        <rect x="18" y="22" width="12" height="36" />
        <rect x="32" y="30" width="10" height="28" />
        <rect x="44" y="20" width="12" height="38" />
        <line x1="22" y1="32" x2="26" y2="32" opacity="0.6" />
        <line x1="22" y1="42" x2="26" y2="42" opacity="0.6" />
        <line x1="48" y1="30" x2="52" y2="30" opacity="0.6" />
        <line x1="48" y1="40" x2="52" y2="40" opacity="0.6" />
        <circle cx="32" cy="10" r="3" fill="currentColor" stroke="none">
          <animate attributeName="r" values="3;4.5;3" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="32" cy="10" r="6" opacity="0.3">
          <animate attributeName="r" values="6;9;6" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.4;0;0.4" dur="2s" repeatCount="indefinite" />
        </circle>
      </svg>
    );
    case 'building': return (
      <svg {...common}>
        {/* Single tall building with floor lines and a glowing window */}
        <rect x="18" y="6" width="28" height="52" />
        <line x1="26" y1="6" x2="26" y2="58" opacity="0.4" />
        <line x1="38" y1="6" x2="38" y2="58" opacity="0.4" />
        <line x1="18" y1="16" x2="46" y2="16" opacity="0.5" />
        <line x1="18" y1="26" x2="46" y2="26" opacity="0.5" />
        <line x1="18" y1="36" x2="46" y2="36" opacity="0.5" />
        <line x1="18" y1="46" x2="46" y2="46" opacity="0.5" />
        <rect x="20" y="48" width="6" height="8" fill="currentColor" stroke="none" opacity="0.7">
          <animate attributeName="opacity" values="0.7;0.25;0.7" dur="2.4s" repeatCount="indefinite" />
        </rect>
        <rect x="40" y="28" width="4" height="6" fill="currentColor" stroke="none" opacity="0.45">
          <animate attributeName="opacity" values="0.45;0.95;0.45" dur="2.4s" begin="0.6s" repeatCount="indefinite" />
        </rect>
        <line x1="14" y1="58" x2="50" y2="58" />
      </svg>
    );
    case 'masterplan': return (
      <svg {...common}>
        <rect x="8" y="8" width="48" height="48" rx="2" />
        <line x1="8" y1="24" x2="56" y2="24" opacity="0.5" />
        <line x1="8" y1="40" x2="56" y2="40" opacity="0.5" />
        <line x1="24" y1="8" x2="24" y2="56" opacity="0.5" />
        <line x1="40" y1="8" x2="40" y2="56" opacity="0.5" />
        <circle cx="24" cy="24" r="3" fill="currentColor" stroke="none" />
        <circle cx="40" cy="40" r="3" fill="currentColor" stroke="none">
          <animate attributeName="opacity" values="1;0.4;1" dur="2s" repeatCount="indefinite" />
        </circle>
      </svg>
    );
    case 'map': return (
      <svg {...common}>
        <path d="M10 16 L24 10 L40 16 L54 10 L54 48 L40 54 L24 48 L10 54 Z" />
        <line x1="24" y1="10" x2="24" y2="48" opacity="0.55" />
        <line x1="40" y1="16" x2="40" y2="54" opacity="0.55" />
        <path d="M32 22 a4 4 0 1 1 0 8 a4 4 0 1 1 0 -8" fill="currentColor" stroke="none" />
        <path d="M32 30 L32 38" stroke="currentColor" />
      </svg>
    );
    case 'units': return (
      <svg {...common}>
        <rect x="8" y="10" width="48" height="44" rx="2" />
        <line x1="32" y1="10" x2="32" y2="54" />
        <line x1="8" y1="32" x2="32" y2="32" />
        <line x1="32" y1="22" x2="56" y2="22" />
        <line x1="32" y1="42" x2="56" y2="42" />
        <rect x="14" y="16" width="6" height="6" fill="currentColor" stroke="none" opacity="0.6" />
        <rect x="38" y="28" width="6" height="6" fill="currentColor" stroke="none" opacity="0.45">
          <animate attributeName="opacity" values="0.45;1;0.45" dur="2.4s" repeatCount="indefinite" />
        </rect>
      </svg>
    );
    case 'panorama': return (
      <svg {...common}>
        <ellipse cx="32" cy="32" rx="22" ry="14" />
        <ellipse cx="32" cy="32" rx="22" ry="14" transform="rotate(60 32 32)" opacity="0.4" />
        <ellipse cx="32" cy="32" rx="22" ry="14" transform="rotate(-60 32 32)" opacity="0.4" />
        <circle cx="32" cy="32" r="3" fill="currentColor" stroke="none">
          <animate attributeName="r" values="2;4;2" dur="2s" repeatCount="indefinite" />
        </circle>
      </svg>
    );
    case 'compare': return (
      <svg {...common}>
        <rect x="8"  y="14" width="20" height="36" rx="2" />
        <rect x="36" y="14" width="20" height="36" rx="2" />
        <line x1="14" y1="22" x2="22" y2="22" opacity="0.6" />
        <line x1="14" y1="30" x2="22" y2="30" opacity="0.6" />
        <line x1="14" y1="38" x2="22" y2="38" opacity="0.45" />
        <line x1="42" y1="22" x2="50" y2="22" opacity="0.6" />
        <line x1="42" y1="30" x2="50" y2="30" opacity="0.6" />
        <line x1="42" y1="38" x2="50" y2="38" opacity="0.45" />
        <line x1="32" y1="10" x2="32" y2="54" opacity="0.5" strokeDasharray="2 2" />
      </svg>
    );
    case 'leads': return (
      <svg {...common}>
        <path d="M20 10 a12 12 0 0 1 24 0 v6 a12 12 0 0 1 -24 0 Z" />
        <path d="M14 56 a18 12 0 0 1 36 0" />
        <circle cx="48" cy="14" r="6" fill="currentColor" stroke="none">
          <animate attributeName="opacity" values="1;0.4;1" dur="1.8s" repeatCount="indefinite" />
        </circle>
        <path d="M45 14 L47 16 L51 12" stroke="#FFFFFF" strokeWidth="2" />
      </svg>
    );
    case 'languages': return (
      <svg {...common}>
        <circle cx="32" cy="32" r="22" />
        <ellipse cx="32" cy="32" rx="9" ry="22" opacity="0.55" />
        <line x1="10" y1="32" x2="54" y2="32" />
        <text x="20" y="26" fontSize="10" fontWeight="700" fill="currentColor" stroke="none">EN</text>
        <text x="32" y="44" fontSize="10" fontWeight="700" fill="currentColor" stroke="none">AR</text>
      </svg>
    );
    default: return (
      <svg {...common}>
        <circle cx="32" cy="32" r="20" />
      </svg>
    );
  }
}

/* ── CUSTOM VIDEO CONTROLS — same UX as TwinMs dossier player ──────────
   Always-visible bar (play/pause, scrubber + time, mute, share,
   fullscreen). Reuses the .dz-tmsc-hero__player* CSS classes (cross-used
   across TwinMs and TwinMaq pages — same pattern as .dz-tms-hero__video-wrap
   being reused on this page's laptop bezel). Fullscreen target is the
   .dz-macbook-bezel element (the rounded laptop screen). */
function TmqVideoControls({ videoRef }) {
  const scrubRef = useTmqRef(null);
  const [playing,     setPlaying]     = useTmqState(false);
  const [muted,       setMuted]       = useTmqState(false);
  const [currentTime, setCurrentTime] = useTmqState(0);
  const [duration,    setDuration]    = useTmqState(0);
  const [scrubbing,   setScrubbing]   = useTmqState(false);
  const [shared,      setShared]      = useTmqState(false);
  const [isFs,        setIsFs]        = useTmqState(false);

  /* Wire up listeners on the externally-owned <video> element */
  useTmqEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay   = ()  => setPlaying(true);
    const onPause  = ()  => setPlaying(false);
    const onTime   = ()  => setCurrentTime(v.currentTime);
    const onMeta   = ()  => { setDuration(v.duration); setMuted(v.muted); };
    const onVolume = ()  => setMuted(v.muted);
    v.addEventListener('play',           onPlay);
    v.addEventListener('pause',          onPause);
    v.addEventListener('timeupdate',     onTime);
    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('volumechange',   onVolume);
    if (v.readyState >= 1) onMeta();
    return () => {
      v.removeEventListener('play',           onPlay);
      v.removeEventListener('pause',          onPause);
      v.removeEventListener('timeupdate',     onTime);
      v.removeEventListener('loadedmetadata', onMeta);
      v.removeEventListener('volumechange',   onVolume);
    };
  }, []);

  /* Sync fullscreen button state with the document's fullscreen element */
  useTmqEffect(() => {
    const sync = () => setIsFs(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener('fullscreenchange',       sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange',       sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  function fmt(t) {
    if (!isFinite(t) || t < 0) t = 0;
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return m + ':' + (s < 10 ? '0' + s : s);
  }

  function togglePlay() {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
    else          { v.pause(); }
  }

  function toggleMute() {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  }

  function toggleFullscreen() {
    const v = videoRef.current;
    if (!v) return;
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen)            document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      return;
    }
    const bezel = v.closest('.dz-macbook-bezel');
    if (bezel && bezel.requestFullscreen) {
      const p = bezel.requestFullscreen();
      if (p && p.catch) p.catch(() => {});
    } else if (bezel && bezel.webkitRequestFullscreen) {
      bezel.webkitRequestFullscreen();
    } else if (v.webkitEnterFullscreen) {
      v.webkitEnterFullscreen();
    }
  }

  function shareVideo() {
    const url   = window.location.href;
    const title = document.title;
    const text  = (document.querySelector('meta[name="description"]') || {}).content || '';
    if (navigator.share) {
      navigator.share({ url, title, text }).catch(() => {});
      return;
    }
    const onSuccess = () => { setShared(true); setTimeout(() => setShared(false), 2000); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(onSuccess).catch(() => {});
    } else {
      const ta = document.createElement('textarea');
      ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); onSuccess(); } catch (e) {}
      document.body.removeChild(ta);
    }
  }

  function pctFromEvent(e) {
    const bar = scrubRef.current;
    if (!bar) return 0;
    const rect = bar.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    return Math.max(0, Math.min(1, x / rect.width));
  }
  function seekToEvent(e) {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    v.currentTime = pctFromEvent(e) * v.duration;
  }
  function onScrubStart(e) { setScrubbing(true); seekToEvent(e); e.preventDefault(); }

  useTmqEffect(() => {
    if (!scrubbing) return;
    const onMove = (e) => seekToEvent(e);
    const onUp   = ()  => setScrubbing(false);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup',   onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup',   onUp);
    };
  });

  const pct = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="dz-tmsc-hero__player" role="group" aria-label="Video controls">
      <button type="button"
              className="dz-tmsc-hero__player-btn"
              onClick={togglePlay}
              aria-label={playing ? 'Pause' : 'Play'}>
        {playing ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <rect x="6" y="5" width="4" height="14" rx="1" />
            <rect x="14" y="5" width="4" height="14" rx="1" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M7 5v14l12-7L7 5z" />
          </svg>
        )}
      </button>

      <div className="dz-tmsc-hero__player-scrub-wrap">
        <div className="dz-tmsc-hero__player-scrub"
             ref={scrubRef}
             role="slider"
             aria-label="Seek"
             aria-valuemin="0"
             aria-valuemax={Math.round(duration) || 0}
             aria-valuenow={Math.round(currentTime)}
             onPointerDown={onScrubStart}>
          <div className="dz-tmsc-hero__player-scrub-fill"  style={{ width: pct + '%' }} />
          <div className="dz-tmsc-hero__player-scrub-thumb" style={{ left:  pct + '%' }} />
        </div>
      </div>

      <div className="dz-tmsc-hero__player-time" aria-hidden="true">
        <span>{fmt(currentTime)}</span>
        <span className="dz-tmsc-hero__player-time-sep"> / </span>
        <span className="dz-tmsc-hero__player-time-total">{fmt(duration)}</span>
      </div>

      <button type="button"
              className="dz-tmsc-hero__player-btn dz-tmsc-hero__player-btn--mute"
              onClick={toggleMute}
              aria-label={muted ? 'Unmute' : 'Mute'}>
        {muted ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" strokeLinecap="round" strokeLinejoin="round" />
            <line x1="22" y1="9"  x2="16" y2="15" strokeLinecap="round" />
            <line x1="16" y1="9"  x2="22" y2="15" strokeLinecap="round" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      <button type="button"
              className={'dz-tmsc-hero__player-btn dz-tmsc-hero__player-btn--share' + (shared ? ' is-shared' : '')}
              onClick={shareVideo}
              aria-label="Share this video">
        {shared ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <polyline points="5 12 10 17 19 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="16 6 12 2 8 6" strokeLinecap="round" strokeLinejoin="round" />
            <line x1="12" y1="2" x2="12" y2="15" strokeLinecap="round" />
          </svg>
        )}
      </button>
      {shared && <span className="dz-tmsc-hero__player-toast" role="status">{dzT('Link copied', 'تم نسخ الرابط')}</span>}

      <button type="button"
              className="dz-tmsc-hero__player-btn dz-tmsc-hero__player-btn--fs"
              onClick={toggleFullscreen}
              aria-label={isFs ? 'Exit fullscreen' : 'Enter fullscreen'}>
        {isFs ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <polyline points="4 10 10 10 10 4"   strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="20 10 14 10 14 4"  strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="14 20 14 14 20 14" strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="10 20 10 14 4 14"  strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <polyline points="9 3 3 3 3 9"       strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="15 3 21 3 21 9"    strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="21 15 21 21 15 21" strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="3 15 3 21 9 21"    strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
    </div>
  );
}

/* ── HERO ─────────────────────────────────────────────────────────────── */
/* Swap auto-loading product videos to their -mobile.mp4 variants on
   viewports ≤800px. Same pattern as Hero.jsx. Use only for autoplay-loop
   videos; keep desktop quality for click-to-play videos with controls. */
function dzMobileVideo(url) {
  if (typeof window === 'undefined' || !url) return url;
  if (!window.matchMedia || !window.matchMedia('(max-width: 800px)').matches) return url;
  return url.replace(/(\.mp4)(\?[^"'\s]*)?$/i, '-mobile$1$2');
}

/* TmqHeroVideo — click-to-play video with custom Play overlay.
   Mirrors TwinMs TmsHeroVideo so both pages have the same UX:
   audio enabled, visitor taps Play, native controls (incl. volume)
   reveal after first interaction. */
function TmqHeroVideo({ src, poster, label }) {
  const videoRef = useTmqRef(null);
  const [hasStarted, setHasStarted] = useTmqState(false);

  const handlePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    setHasStarted(true);
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };

  return (
    <div className="dz-macbook">
      <div className="dz-macbook-screen">
        <span className="dz-macbook-notch" aria-hidden="true" />
        <div className={`dz-macbook-bezel dz-tms-hero__video-wrap${hasStarted ? ' is-playing' : ''}`}>
          <video
            ref={videoRef}
            src={src}
            poster={poster}
            playsInline
            preload="metadata"
            disablePictureInPicture
            disableRemotePlayback
            controlsList="nodownload noremoteplayback"
            aria-label={label || 'TwinMaq platform tour'}
            onClick={() => {
              const v = videoRef.current;
              if (!v) return;
              if (v.paused) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
              else          { v.pause(); }
            }}
          />
          {!hasStarted && (
            <button
              type="button"
              className="dz-tms-hero__play"
              onClick={handlePlay}
              aria-label="Play TwinMaq platform tour"
            >
              <span className="dz-tms-hero__play-circle" aria-hidden="true">
                <svg viewBox="0 0 80 80" width="60" height="60" fill="none">
                  <circle cx="40" cy="40" r="38" fill="#4FC8F4" />
                  <polygon points="32,26 32,54 56,40" fill="#0F1E2C" />
                </svg>
              </span>
              <span className="dz-tms-hero__play-label">{dzT('Watch the platform tour', 'شاهد جولة المنصّة')}</span>
            </button>
          )}
          {hasStarted && <TmqVideoControls videoRef={videoRef} />}
        </div>
      </div>
      <span className="dz-macbook-base" aria-hidden="true" />
    </div>
  );
}

function TmqHero({ data }) {
  const ref = useTmqReveal(0.05);
  return (
    <section className="dz-tmq-hero" ref={ref}>
      <div className="dz-tmq-hero__inner">
        <div className="dz-tmq-hero__copy">
          {data.logo && (
            <img className="dz-tmq-hero__logo" src={data.logo} alt="TwinMaq" />
          )}
          <span className="dz-eyebrow dz-tms-eyebrow dz-tms-eyebrow--on-dark">{data.eyebrow}</span>
          <h1 className="dz-tmq-hero__title">
            <span className="dz-tmq-hero__title-lead">{data.title.lead}</span>
            <span className="dz-tmq-hero__title-accent">{data.title.accent}</span>
          </h1>
          <p className="dz-tmq-hero__sub">{data.sub}</p>
          {data.cta && (
            <div className="dz-tmq-hero__cta" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
              <a className="dz-btn dz-btn--aqua" href={data.cta.href}>
                {data.cta.label}
                <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" style={{ transform: dzIsAr() ? 'scaleX(-1)' : 'none' }}>
                  <path d="M3 8h9M8 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
              {data.cta2 && (
                <a className="dz-btn dz-btn--ghost" href={data.cta2.href} target="_blank" rel="noopener" style={{ color: 'var(--dz-navy, #2B3D4F)', borderColor: 'rgba(43, 61, 79, 0.35)' }}>
                  {data.cta2.label}
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M14 4h6v6M20 4l-9 9M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </a>
              )}
            </div>
          )}

          {data.badge && (
            <div className="dz-tmq-hero__badge">
              <span className="dz-tmq-hero__badge-dot" aria-hidden="true" />
              <span className="dz-tmq-hero__badge-kicker">{data.badge.kicker}</span>
              <span className="dz-tmq-hero__badge-sep">·</span>
              <span className="dz-tmq-hero__badge-name">{data.badge.name}</span>
              {data.badge.note && (
                <>
                  <span className="dz-tmq-hero__badge-sep">·</span>
                  <span className="dz-tmq-hero__badge-note">{data.badge.note}</span>
                </>
              )}
            </div>
          )}

          {data.stats && (
            <ul className="dz-tmq-hero__stats">
              {data.stats.map((s, i) => (
                <li key={i} style={{ '--tmq-i': i }}>
                  <span className="dz-tmq-hero__stat-num">{s.value}{s.suffix}</span>
                  <span className="dz-tmq-hero__stat-lbl">{s.label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="dz-tmq-hero__media">
          <TmqHeroVideo
            src={dzMobileVideo(data.video)}
            poster={data.poster}
            label="TwinMaq platform tour — 2D CAD to navigable 3D experience"
          />
        </div>
      </div>
    </section>
  );
}

/* ── JOURNEY — 5-step horizontal flow ─────────────────────────────────── */
function TmqJourney({ data }) {
  const ref = useTmqReveal(0.15);
  return (
    <section className="dz-tmq-journey" ref={ref}>
      <div className="dz-tmq-journey__head">
        <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-tmq-h2">{data.title}</h2>
        <p className="dz-tmq-lead">{data.lead}</p>
      </div>
      <ol className="dz-tmq-journey__steps">
        {data.steps.map((s, i) => (
          <li className="dz-tmq-step" key={i} style={{ '--tmq-i': i }}>
            <span className="dz-tmq-step__icon" aria-hidden="true">
              <TmqGlyph name={s.glyph} />
            </span>
            <span className="dz-tmq-step__connector" aria-hidden="true" />
            <h3 className="dz-tmq-step__label">{s.label}</h3>
            <p className="dz-tmq-step__desc">{s.desc}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* ── FEATURES — 8-tile feature grid ───────────────────────────────────── */
function TmqFeatures({ data }) {
  const ref = useTmqReveal(0.12);
  return (
    <section className="dz-tmq-features" ref={ref}>
      <div className="dz-tmq-features__head">
        <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-tmq-h2">{data.title}</h2>
        <p className="dz-tmq-lead">{data.lead}</p>
      </div>
      <div className="dz-tmq-features__grid">
        {data.tiles.map((t, i) => (
          <article className="dz-tmq-ftile" key={i} style={{ '--tmq-i': i }}>
            <div className="dz-tmq-ftile__glyph"><TmqGlyph name={t.glyph} /></div>
            <div className="dz-tmq-ftile__label">{t.label}</div>
            <p className="dz-tmq-ftile__text">{t.text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

/* ── LIVE PROJECT — Aleen Khobar ─────────────────────────────────────── */
function TmqLive({ data }) {
  const ref = useTmqReveal(0.15);
  return (
    <section className="dz-tmq-live" ref={ref}>
      <div className="dz-tmq-live__inner">
        <div className="dz-tmq-live__copy">
          <span className="dz-eyebrow dz-tms-eyebrow dz-tms-eyebrow--on-dark">{data.eyebrow}</span>
          <h2 className="dz-tmq-h2 dz-tmq-h2--on-dark">{data.title}</h2>
          <p className="dz-tmq-lead dz-tmq-lead--on-dark">{data.lead}</p>
          <dl className="dz-tmq-live__facts">
            {data.facts.map((f, i) => (
              <div className="dz-tmq-live__fact" key={i} style={{ '--tmq-i': i }}>
                <dt>{f.label}</dt>
                <dd>{f.value}</dd>
              </div>
            ))}
          </dl>
          {data.caseHref && (
            <a className="dz-tmq-live__case-cta" href={data.caseHref}>
              {data.caseLabel || 'Open full case study'}
              <span aria-hidden="true">→</span>
            </a>
          )}
        </div>
        <figure className="dz-tmq-live__media">
          <img src={data.image} alt={data.alt} loading="lazy" decoding="async" />
        </figure>
      </div>
    </section>
  );
}

/* ── CAPABILITIES — asymmetric duo (lead-story layout) + closing CTA ──
   Restructured 2026-05-06 from a 2×2 identical-card grid (banned per
   PRODUCT.md anti-references) into one featured card + three text-only
   minis. The featured card carries weight via dark-navy fill against the
   white section bg, fixing the pale-on-pale contrast complaint. The three
   minis read as supporting details, not as four equal-weight things. */
function TmqCapabilities({ data }) {
  const ref = useTmqReveal(0.12);
  const featuredIndex = 1;            // 'Web platform' — most concrete user-facing capability
  const featured = data.items[featuredIndex];
  const minis    = data.items.filter((_, i) => i !== featuredIndex);
  return (
    <section className="dz-tmq-cap" ref={ref}>
      <div className="dz-tmq-cap__head">
        <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-tmq-h2">{data.title}</h2>
        <p className="dz-tmq-lead">{data.lead}</p>
      </div>
      <div className="dz-tmq-cap__grid">
        {/* Featured — image-led card, real product UI shot from Aleen 152.
            Per PRODUCT.md: real assets win. The screenshot IS the proof. */}
        <article className="dz-tmq-cap__featured">
          <span className="dz-tmq-cap__featured-kicker">
            <span className="dz-tmq-cap__featured-dot" aria-hidden="true" />
            {featured.kicker} · {dzT('In production', 'في الإنتاج')}
          </span>
          <h3 className="dz-tmq-cap__featured-label">{featured.label}</h3>
          <p className="dz-tmq-cap__featured-text">{featured.text}</p>
        </article>
        {/* Minis — text-only column, hairline separators */}
        <ul className="dz-tmq-cap__minis">
          {minis.map((m, i) => (
            <li className="dz-tmq-cap__mini" key={i} style={{ '--tmq-i': i }}>
              <span className="dz-tmq-cap__mini-kicker">
                <span className="dz-tmq-cap__mini-dot" aria-hidden="true" />
                {m.kicker}
              </span>
              <h3 className="dz-tmq-cap__mini-label">{m.label}</h3>
              <p className="dz-tmq-cap__mini-text">{m.text}</p>
            </li>
          ))}
        </ul>
      </div>
      {data.cta && (
        <div className="dz-tmq-cap__cta">
          <p className="dz-tmq-cap__cta-line">{data.cta.line}</p>
          <a className="dz-tmq-cap__cta-btn" href={data.cta.href}>
            {data.cta.label}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
          {data.cta.link && (
            <a className="dz-tmq-cap__cta-link" href={data.cta.link.href} target="_blank" rel="noopener" style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dz-navy, #2B3D4F)', textDecoration: 'underline', textUnderlineOffset: '4px', textDecorationColor: 'rgba(79, 200, 244, 0.8)' }}>
              {data.cta.link.label}
            </a>
          )}
        </div>
      )}
    </section>
  );
}

/* ── Page root ────────────────────────────────────────────────────────── */
/* ── WHAT IS — definitional passage + market context (R4 T1) ─────────── */
function TmqWhatIs({ data }) {
  const ref = useTmqReveal(0.15);
  if (!data) return null;
  return (
    <section className="dz-tmq-whatis" ref={ref}>
      <div className="dz-tmq-whatis__inner">
        <span className="dz-eyebrow dz-tms-eyebrow dz-tms-eyebrow--on-dark">{data.eyebrow}</span>
        <h2 className="dz-tmq-h2 dz-tmq-h2--on-dark">{data.title}</h2>
        <div className="dz-tmq-whatis__body">
          {data.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </div>
    </section>
  );
}

/* ── HOW IT'S BUILT — 4-step delivery spine (R4 T3) ──────────────────── */
function TmqHowBuilt({ data }) {
  const ref = useTmqReveal(0.12);
  if (!data) return null;
  return (
    <section className="dz-tmq-howbuilt" ref={ref}>
      <div className="dz-tmq-howbuilt__head">
        <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-tmq-h2">{data.title}</h2>
        <p className="dz-tmq-lead">{data.lead}</p>
      </div>
      <ol className="dz-tmq-howbuilt__steps">
        {data.steps.map((s, i) => (
          <li className="dz-tmq-howbuilt__step" key={i} style={{ '--tmq-i': i }}>
            <span className="dz-tmq-howbuilt__step-num" aria-hidden="true">{i + 1}</span>
            <h3 className="dz-tmq-howbuilt__step-label">{s.label}</h3>
            <p className="dz-tmq-howbuilt__step-text">{s.text}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* ── OUTCOMES — why it works (R4 T2). Paragraphs may carry inline
   citation links (attributed sources), so they render via
   dangerouslySetInnerHTML — same pattern as WhyDZ slides on home. ────── */
function TmqOutcomes({ data }) {
  const ref = useTmqReveal(0.15);
  if (!data) return null;
  return (
    <section className="dz-tmq-outcomes" ref={ref}>
      <div className="dz-tmq-outcomes__inner">
        <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-tmq-h2">{data.title}</h2>
        {/* Design pass: attributed stat cards — same figures + sources as the
            prose below, lifted into a scannable strip. */}
        {data.stats && (
          <ul className="dz-tmq-outcomes__stats">
            {data.stats.map((s, i) => (
              <li key={i} className="dz-tmq-outcomes__stat" style={{ '--tmq-i': i }}>
                <span className="dz-tmq-outcomes__stat-value">{s.value}</span>
                <span className="dz-tmq-outcomes__stat-label">{s.label}</span>
                <a className="dz-tmq-outcomes__stat-source" href={s.href} target="_blank" rel="noopener">{s.source}</a>
              </li>
            ))}
          </ul>
        )}
        <div className="dz-tmq-outcomes__body">
          {data.paragraphs.map((p, i) => <p key={i} dangerouslySetInnerHTML={{ __html: p }} />)}
        </div>
      </div>
    </section>
  );
}

/* ── COMPARISON — one twin vs the patchwork (R4 T4) ──────────────────── */
function TmqComparison({ data }) {
  const ref = useTmqReveal(0.12);
  if (!data) return null;
  return (
    <section className="dz-tmq-compare" ref={ref}>
      <div className="dz-tmq-compare__head">
        <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-tmq-h2">{data.title}</h2>
        <p className="dz-tmq-lead">{data.lead}</p>
      </div>
      <div className="dz-tmq-compare__scroll" role="region" aria-label={data.tableAriaLabel} tabIndex={0}>
        <table className="dz-tmq-compare__table">
          <thead>
            <tr>
              <th scope="col" aria-label={data.firstColLabel}></th>
              {data.cols.map((c, i) => (
                <th scope="col" key={i} className={i === 0 ? 'dz-tmq-compare__hero' : undefined}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={i}>
                <th scope="row">{r.label}</th>
                {r.cells.map((cell, j) => (
                  <td key={j} className={j === 0 ? 'dz-tmq-compare__hero' : undefined}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ── FAQ — product Q&A (R4 T5) — exact home FAQ interaction pattern:
   lead + toggle button reveals the list; each question is a <details>
   accordion. All Q&A text stays in the DOM (hidden attr) → crawlable. ── */
function TmqFaq({ data }) {
  const ref = useTmqReveal(0.08);
  if (!data) return null;
  return (
    <section className="dz-section dz-faq dz-tmq-faqsec" ref={ref} aria-label={data.sectionAriaLabel}>
      <div className="dz-section__inner">
        <span className="dz-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-h2">{data.titleLeading} <em>{data.titleAccent}</em></h2>
        <p className="dz-lead">{data.lead}</p>
        <div className="dz-faq__list" id="dz-tmq-faq-list">
          {data.items.map((it, i) => (
            <details key={i} className="dz-faq-item">
              <summary>{it.q}</summary>
              <div className="dz-faq-item__body">{it.a}</div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function TwinMaqPage() {
  const data = window.DZ_TWINMAQ;
  if (!data) {
    return (
      <main className="dz-tmq dz-tmq-missing">
        <h1>TwinMaq</h1>
        <p>Content layer failed to load.</p>
      </main>
    );
  }
  return (
    <main className="dz-tmq" id="dz-main">
      <TmqHero          data={data.hero} />
      <TmqWhatIs        data={data.whatIs} />
      <TmqJourney       data={data.journey} />
      <TmqFeatures      data={data.features} />
      <TmqHowBuilt      data={data.howBuilt} />
      <TmqLive          data={data.live} />
      <TmqOutcomes      data={data.outcomes} />
      <TmqComparison    data={data.comparison} />
      <TmqCapabilities  data={data.capabilities} />
      <TmqFaq           data={data.faq} />
    </main>
  );
}

window.TwinMaqPage = TwinMaqPage;
